import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import ErrorMessage from "./ErrorMessage";
import { useRegistryData } from "./hooks/useRegistryData";
import { useReleaseLabelDialogState } from "./hooks/useReleaseLabelDialogState";
import { useReleaseLabelSubmission } from "./hooks/useReleaseLabelSubmission";
import NoLabelsMessage from "./NoLabelsMessage";
import RegistrySelector from "./RegistrySelector";
import ReleaseLabelSelector from "./ReleaseLabelSelector";
import SelectedRegistriesGrid from "./SelectedRegistriesGrid";
import { CreateReleaseLabelDialogProps } from "./types";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/LoadingSpinner";

enum DialogStep {
  SELECT_LABEL = "SELECT_LABEL",
  SELECT_REGISTRIES = "SELECT_REGISTRIES",
}

const CreateReleaseLabelDialog: FC<CreateReleaseLabelDialogProps> = ({
  isOpen,
  onSubmit,
  setOpen,
  onLoadingChange,
}) => {
  const [currentStep, setCurrentStep] = useState<DialogStep>(DialogStep.SELECT_LABEL);

  const { folderId } = useParams();

  const {
    selectedReleaseLabel,
    errorMessage,
    selectedRegistryIds,
    selectedRegistry,
    availableReleaseLabelsList,
    userToken,
    userContext,
    setSelectedRegistry,
    setErrorMessage,
    handleReleaseLabelSelect,
    handleAddRegistry,
    handleRemoveRegistry,
  } = useReleaseLabelDialogState(isOpen);

  const { ref, registryObjectsList, selectedRegistries, availableRegistries, hasNextPage } =
    useRegistryData(
      userToken!,
      userContext.activeWorkspaceId!,
      selectedReleaseLabel,
      selectedRegistryIds,
    );

  const { isLoading, submitReleaseLabelSelection, submitExperimentCreation } =
    useReleaseLabelSubmission(
      userToken!,
      userContext.activeWorkspaceId!,
      folderId ? Number(folderId) : null,
    );

  useEffect(() => {
    if (!isOpen || !selectedReleaseLabel) {
      setCurrentStep(DialogStep.SELECT_LABEL);
    }
  }, [isOpen, selectedReleaseLabel]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const handleBack = () => {
    if (currentStep === DialogStep.SELECT_REGISTRIES) {
      setCurrentStep(DialogStep.SELECT_LABEL);
      setSelectedRegistry(null);
      setErrorMessage(null);
    }
  };

  const handleContinue = async () => {
    if (currentStep === DialogStep.SELECT_LABEL) {
      if (!selectedReleaseLabel) {
        return;
      }

      if (registryObjectsList.length > 0) {
        setCurrentStep(DialogStep.SELECT_REGISTRIES);
        setErrorMessage(null);
      } else {
        setErrorMessage(null);
        submitReleaseLabelSelection(selectedReleaseLabel, onSubmit);
        setOpen(false);
      }
    } else if (currentStep === DialogStep.SELECT_REGISTRIES) {
      if (selectedRegistryIds.length === 0) {
        return;
      }

      try {
        setErrorMessage(null);
        await submitExperimentCreation(selectedReleaseLabel, selectedRegistryIds);
        setOpen(false);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage("An unexpected error occurred");
        }
      }
    }
  };

  const getSubmitButtonText = () => {
    if (currentStep === DialogStep.SELECT_REGISTRIES && registryObjectsList.length > 0) {
      return "Build Experiment";
    }

    return "Continue";
  };

  const isSubmitDisabled = () => {
    if (currentStep === DialogStep.SELECT_LABEL) {
      return !selectedReleaseLabel;
    }
    if (currentStep === DialogStep.SELECT_REGISTRIES) {
      return selectedRegistryIds.length === 0;
    }
    return false;
  };

  const getDialogTitle = () => {
    if (currentStep === DialogStep.SELECT_REGISTRIES) {
      return "Add Prompt Templates";
    }
    return "Create A/B Test";
  };

  const getDialogDescription = () => {
    if (currentStep === DialogStep.SELECT_REGISTRIES) {
      return (
        <span>
          Select which prompt templates with the label <strong>{selectedReleaseLabel}</strong> to
          include in this experiment.
        </span>
      );
    }

    return (
      <span>
        Choose a release label to build an experiment. The release label must already be configured
        on your prompt templates.
      </span>
    );
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog onOpenChange={handleClose} open={isOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {currentStep === DialogStep.SELECT_LABEL && (
            <>
              {availableReleaseLabelsList.length === 0 ? (
                <NoLabelsMessage />
              ) : (
                <ReleaseLabelSelector
                  selectedReleaseLabel={selectedReleaseLabel}
                  availableReleaseLabelsList={availableReleaseLabelsList}
                  onReleaseLabelSelect={handleReleaseLabelSelect}
                />
              )}
            </>
          )}

          {currentStep === DialogStep.SELECT_REGISTRIES && (
            <>
              <SelectedRegistriesGrid
                selectedRegistries={selectedRegistries}
                onRemoveRegistry={handleRemoveRegistry}
              />

              {selectedRegistries.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                  <p className="text-sm text-gray-600">No prompt templates selected yet.</p>
                  <p className="mt-1 text-xs text-gray-500">Add at least one to continue.</p>
                </div>
              )}

              <RegistrySelector
                availableRegistries={availableRegistries}
                selectedRegistry={selectedRegistry}
                onSelectRegistry={setSelectedRegistry}
                onAddRegistry={handleAddRegistry}
              />

              {hasNextPage && (
                <div ref={ref} className="flex justify-center py-2">
                  <LoadingSpinner size={5} />
                </div>
              )}
            </>
          )}
        </div>

        <ErrorMessage message={errorMessage} />

        <DialogFooter>
          {currentStep === DialogStep.SELECT_REGISTRIES ? (
            <Button onClick={handleBack} variant="outline" disabled={isLoading}>
              Back
            </Button>
          ) : (
            <Button onClick={handleClose} variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          )}

          {availableReleaseLabelsList.length > 0 && (
            <Button
              isLoading={isLoading}
              onClick={handleContinue}
              disabled={isSubmitDisabled() || isLoading}
            >
              {getSubmitButtonText()}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReleaseLabelDialog;
